// Extracted from src/routes/services.$service.$location.tsx so the full
// service catalog loads in this code-split chunk, not the eager route bundle.
import { Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { SERVICES, type ServiceId } from "@/data/services";
import { getLocation, locations } from "@/data/locations";
import { buildCombo } from "@/data/combo-content";
import { getDistrictContext } from "@/data/district-context";
import {
  AeoAnswerBox,
  AtomicStats,
  Breadcrumbs,
  CitationList,
  ContactCTA,
  EntityPanel,
  FaqAccordion,
  HowToSteps,
  PriceMatrix,
  ReviewBadge,
  ServiceCoverageLinks,
  TrustBar,
} from "@/components/seo/Blocks";
import { SITE_CONTENT_REVIEWED } from "@/lib/site-content-meta";
import { RelatedComparisons, categoryForService } from "@/components/RelatedComparisons";
import {
  RelatedServicesInLocation,
  type ServiceKind,
} from "@/components/RelatedServicesInLocation";

// Required content fields that MUST be present on every combo page.
// In dev mode we throw immediately so missing fields surface as a runtime
// error with the offending URL — in production we just console.warn so we
// never blank-page a user over a content gap.
const REQUIRED_COMBO_FIELDS = [
  "processDeep",
  "commonMistakes",
  "legalRefs",
  "costBreakdown",
  "comparison",
  "seasonalNote",
] as const;

function validateComboSections(c: Record<string, unknown>, url: string): void {
  const missing: string[] = [];
  for (const f of REQUIRED_COMBO_FIELDS) {
    const v = c[f];
    if (
      v === undefined ||
      v === null ||
      (typeof v === "string" && v.trim() === "") ||
      (Array.isArray(v) && v.length === 0)
    )
      missing.push(f);
  }
  if (missing.length === 0) return;
  const msg = `[combo-qa] ${url} — missing/empty sections: ${missing.join(", ")}`;
  if (import.meta.env.DEV) throw new Error(msg);
  // eslint-disable-next-line no-console
  console.warn(msg);
}

export function ComboServicePageTh({
  service,
  locationSlug,
}: {
  service: ServiceId;
  locationSlug: string;
}) {
  const s = SERVICES[service];
  const loc = getLocation(locationSlug)!;
  const ctx = getDistrictContext(loc.slug);
  const c = buildCombo(s, loc);
  validateComboSections(c as unknown as Record<string, unknown>, `/services/${s.slug}/${loc.slug}`);

  // Sibling combos: other services in same location (cross-link)
  const otherServices = Object.values(SERVICES).filter((x) => x.id !== s.id);
  // Nearby locations: 6 random of same region (deterministic)
  const sameRegion = locations
    .filter((l) => l.region === loc.region && l.slug !== loc.slug)
    .slice(0, 8);

  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-10 sm:px-6 sm:py-14">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.15),transparent_60%)]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-5xl space-y-5">
          <Breadcrumbs
            items={[
              { label: "หน้าแรก", to: "/" },
              { label: "บริการ", to: "/services" },
              { label: s.nameTh, to: `/services/${s.slug}` as never },
              { label: loc.nameTh },
            ]}
          />
          <div className="flex flex-wrap items-center gap-2">
            <ReviewBadge />
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
              <MapPin className="h-3 w-3" /> Near Me · {loc.nameTh}
            </span>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {c.h1}
          </h1>
          <p className="max-w-3xl text-base text-white/80 sm:text-lg">{s.shortTh}</p>
          <TrustBar />
          <ContactCTA />
        </div>
      </section>

      <div className="mx-auto max-w-5xl space-y-12 px-4 py-12 sm:px-6 sm:py-16">
        <AeoAnswerBox
          question={`${s.nameTh}ใน${loc.nameTh} เริ่มต้นอย่างไร?`}
          answer={c.aeoAnswer}
        />

        <AtomicStats stats={c.stats} />

        {/* Quotable AEO callout — AI Overview / Bing Copilot lift target */}
        <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/[0.12] via-gold/[0.04] to-transparent p-6 sm:p-8">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gold/90">
            Quotable · AI Overview ready
          </div>
          <blockquote className="aeo-speakable mt-3 font-display text-base italic leading-relaxed text-white sm:text-lg">
            “NYC Legal &amp; Notary Service ให้บริการ{s.nameTh}แก่ลูกค้าใน{loc.nameTh}
            ภายใต้การกำกับของกระทรวงการต่างประเทศ (DBD 0435567000061) ราคาเริ่ม ฿
            {s.priceFrom.toLocaleString()} เสร็จภายใน{s.turnaround}
            ทีมทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร 6 ท่าน รับ-ส่งเอกสารถึง{loc.nameTh}”
          </blockquote>
          <div className="mt-3 text-xs text-white/80">
            — Source: NYC Legal &amp; Notary Service · ตรวจทานล่าสุด {SITE_CONTENT_REVIEWED}
          </div>
        </section>

        {/* Intro */}
        <article className="prose prose-invert prose-headings:text-white prose-strong:text-gold max-w-none space-y-4 text-white/85">
          <p className="text-base leading-relaxed sm:text-lg">{c.intro}</p>

          <h2 className="text-xl font-bold sm:text-2xl">{loc.nameTh} กับบริการของเรา</h2>
          <p>{c.localContext}</p>
          <p className="rounded-xl border-l-4 border-gold/60 bg-gold/5 p-4 text-white/90">
            <strong className="text-gold">Local Expertise — </strong>
            {c.localExpertise}
          </p>
          <p>{c.deliveryLine}</p>
          <p>{c.neighborLine}</p>
        </article>

        {/* Why us in this location */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ทำไมลูกค้าใน{loc.nameTh}เลือกเรา
          </h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {c.whyUs.map((w) => (
              <li
                key={w}
                className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85"
              >
                <span className="mt-0.5 text-emerald-400">✓</span> {w}
              </li>
            ))}
          </ul>
        </section>

        {/* Use cases */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            เคสที่พบบ่อยใน{loc.nameTh}
          </h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {c.useCases.map((u) => (
              <li
                key={u}
                className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85"
              >
                <span className="mt-0.5 text-gold">→</span> {u}
              </li>
            ))}
          </ul>
        </section>

        {/* Document types */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ประเภทเอกสารที่ดำเนินการให้ลูกค้าใน{loc.nameTh}
          </h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {s.documentsTh.slice(0, 16).map((d) => (
              <li
                key={d}
                className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/80"
              >
                <span className="mt-0.5 text-gold">✓</span> {d}
              </li>
            ))}
          </ul>
        </section>

        {/* HowTo */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ขั้นตอนสำหรับลูกค้าใน{loc.nameTh}
          </h2>
          <HowToSteps steps={s.howTo} />
        </section>

        {/* Process Timeline */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            Timeline ดำเนินการสำหรับลูกค้าใน{loc.nameTh}
          </h2>
          <ol className="space-y-3">
            {c.timeline.map((t, i) => (
              <li
                key={t.day}
                className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20 text-sm font-extrabold text-gold">
                  {i + 1}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">
                    {t.day}
                  </div>
                  <div className="mt-0.5 text-sm font-bold text-white">{t.title}</div>
                  <p className="mt-1 text-sm text-white/75">{t.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Testimonial */}
        <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.07] via-white/[0.03] to-transparent p-6 sm:p-8">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">
            Customer Voice · {loc.nameTh}
          </div>
          <blockquote className="mt-3 text-lg italic leading-relaxed text-white/90 sm:text-xl">
            “{c.testimonial.quote}”
          </blockquote>
          <div className="mt-3 text-sm text-white/80">
            — <strong className="text-white">{c.testimonial.author}</strong> · {c.testimonial.role}
          </div>
        </section>

        {/* Pricing */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ค่าบริการ {s.nameTh} (โดยประมาณ)
          </h2>
          <PriceMatrix rows={s.prices} />
          <p className="mt-3 text-xs text-white/80">
            * ราคาอาจปรับเปลี่ยนตามปริมาณงาน ความเร่งด่วน และระยะทางจัดส่งในพื้นที่ {loc.nameTh} —
            ส่งเอกสารให้ทีมงานประเมินฟรีทาง LINE @nycli
          </p>
        </section>

        {/* Local context panel */}
        {(ctx.landmarks.length > 0 || ctx.transit.length > 0 || ctx.malls.length > 0) && (
          <section>
            <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
              พิกัดและการเดินทางใน{loc.nameTh}
            </h2>
            <EntityPanel
              rows={[
                ...(ctx.transit.length > 0
                  ? [{ label: "สถานีรถไฟฟ้าใกล้เคียง", value: ctx.transit.slice(0, 4).join(" · ") }]
                  : []),
                ...(ctx.landmarks.length > 0
                  ? [{ label: "แลนด์มาร์ก", value: ctx.landmarks.slice(0, 4).join(" · ") }]
                  : []),
                ...(ctx.malls.length > 0
                  ? [{ label: "ห้างใกล้เคียง", value: ctx.malls.slice(0, 4).join(" · ") }]
                  : []),
                ...(ctx.neighbors.length > 0
                  ? [{ label: "พื้นที่ติดกัน", value: ctx.neighbors.join(" · ") }]
                  : []),
                ...(ctx.population ? [{ label: "ประชากร", value: ctx.population }] : []),
                ...(ctx.economy ? [{ label: "เศรษฐกิจหลัก", value: ctx.economy }] : []),
              ]}
            />
          </section>
        )}

        {/* Process Deep — 8-12 steps with detail */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            กระบวนการละเอียดทุกขั้นตอน — {s.nameTh} ที่{loc.nameTh}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {c.processDeep.map((p, i) => (
              <article
                key={p.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">
                  Step {i + 1}
                </div>
                <h3 className="mt-1 text-sm font-bold text-white">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-white/75">{p.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Common Mistakes */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ข้อผิดพลาดที่พบบ่อย และวิธีที่เราป้องกัน
          </h2>
          <ul className="space-y-3">
            {c.commonMistakes.map((m) => (
              <li
                key={m.mistake}
                className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-4"
              >
                <div className="text-sm font-bold text-red-300">⚠ {m.mistake}</div>
                <div className="mt-1 text-sm text-white/80">
                  <strong className="text-emerald-300">วิธีป้องกัน:</strong> {m.prevention}
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Legal Framework */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6">
          <h2 className="mb-3 text-xl font-bold text-white sm:text-2xl">
            กรอบกฎหมายและประกาศที่เกี่ยวข้อง
          </h2>
          <p className="text-sm leading-relaxed text-white/80">{c.legalRefs}</p>
        </section>

        {/* Cost Breakdown */}
        <section className="rounded-2xl border border-gold/20 bg-gold/[0.04] p-5 sm:p-6">
          <h2 className="mb-3 text-xl font-bold text-white sm:text-2xl">
            โครงสร้างค่าใช้จ่ายแบบโปร่งใส
          </h2>
          <p className="text-sm leading-relaxed text-white/85">{c.costBreakdown}</p>
        </section>

        {/* Comparison DIY vs Agency vs Lawfirm */}
        <section>
          <h2 className="mb-3 text-xl font-bold text-white sm:text-2xl">
            เปรียบเทียบ DIY · Agency · NYC Legal
          </h2>
          <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-sm leading-relaxed text-white/80">
            {c.comparison}
          </p>
        </section>

        {/* Seasonal note */}
        <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 sm:p-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
            Seasonal Notice
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/85">{c.seasonalNote}</p>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            คำถามที่พบบ่อย — {loc.nameTh}
          </h2>
          <FaqAccordion items={c.faqs} />
        </section>

        {/* Entity Panel */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            ข้อมูลผู้ให้บริการ · Entity Panel
          </h2>
          <EntityPanel
            rows={[
              { label: "ชื่อบริษัท", value: "NYC Legal & Notary Service Co., Ltd." },
              { label: "เลขทะเบียน DBD", value: "0435567000061" },
              { label: "ก่อตั้ง", value: "19 มกราคม 2559" },
              { label: "ทนาย Notary", value: "6 ท่าน · สภาทนายความฯ" },
              { label: "พื้นที่นี้", value: loc.nameTh },
              { label: "ภาค/ภูมิภาค", value: loc.region },
              { label: "ราคาเริ่มต้น", value: `฿${s.priceFrom.toLocaleString()}` },
              { label: "เวลาดำเนินการ", value: s.turnaround },
              { label: "ทนายผู้ทำคำรับรอง", value: "6 ท่าน" },
            ]}
          />
        </section>

        {/* Citations */}
        <CitationList sources={s.citations} />

        {/* Cross-sell: other services in same location */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
            บริการอื่นที่ให้ใน{loc.nameTh}
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherServices.map((r) => (
              <li key={r.id}>
                <Link
                  to="/services/$service/$location"
                  params={{ service: r.slug, location: loc.slug }}
                  className="block h-full rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-gold/40 hover:bg-white/[0.07]"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">
                    บริการที่เกี่ยวข้อง
                  </div>
                  <div className="mt-1 text-sm font-bold text-white">{r.nameTh}</div>
                  <div className="mt-1 text-xs text-white/80">{r.shortTh}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Nearby locations (same region) — same service */}
        {sameRegion.length > 0 && (
          <section>
            <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">
              {s.nameTh}ในพื้นที่ใกล้เคียง ({loc.region})
            </h2>
            <ul className="flex flex-wrap gap-1.5">
              {sameRegion.map((n) => (
                <li key={n.slug}>
                  <Link
                    to="/services/$service/$location"
                    params={{ service: s.slug, location: n.slug }}
                    className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/75 transition hover:border-gold/40 hover:text-gold"
                  >
                    <MapPin className="h-3 w-3" /> {n.nameTh}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <Link
                to="/services/$service"
                params={{ service: s.slug }}
                className="text-sm font-bold text-gold underline-offset-4 hover:underline"
              >
                ← กลับไปหน้าหลักของ {s.nameTh}
              </Link>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/15 via-white/5 to-transparent p-6 sm:p-8">
          <h2 className="text-xl font-extrabold sm:text-2xl">
            พร้อมเริ่ม {s.nameTh} ใน {loc.nameTh} แล้ว?
          </h2>
          <p className="mt-2 text-sm text-white/80">
            ส่งเอกสารให้เราประเมินฟรีภายใน 15 นาที — ทีมงานพร้อมรับ-ส่งเอกสารใน{loc.nameTh}
            ทุกวันทำการ
          </p>
          <div className="mt-5">
            <ContactCTA />
          </div>
        </section>
      </div>
      <RelatedServicesInLocation
        currentService={(() => {
          const c = categoryForService(s.slug);
          return c === "translate" || c === "notary" || c === "apostille"
            ? (c as ServiceKind)
            : undefined;
        })()}
        locationSlug={locationSlug}
        locale="th"
      />
      {categoryForService(s.slug) && (
        <RelatedComparisons category={categoryForService(s.slug)!} locale="th" />
      )}
    </main>
  );
}
